import { useState, useEffect, useCallback } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Search, Loader2, Eye, FileText, TrendingUp, IndianRupee,
  Percent, AlertTriangle, Upload,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const BASE = `${API}/api/v2/shipment-ledger`;

const NETWORKS = ["All", "OWN", "3PL_B2B", "3PL_B2C"];
const RECON_STATUSES = ["All", "PENDING", "VERIFIED", "DISPUTED", "SETTLED"];

const MID_BADGE = {
  PENDING: "bg-amber-100 text-amber-700",
  SUBMITTED: "bg-orange-100 text-orange-700",
  APPROVED: "bg-emerald-100 text-emerald-700",
};

const RECON_BADGE = {
  PENDING: "bg-amber-100 text-amber-700",
  VERIFIED: "bg-emerald-100 text-emerald-700",
  DISPUTED: "bg-red-100 text-red-700",
  SETTLED: "bg-slate-200 text-slate-700",
};

function fmt(n) {
  return `\u20B9${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function ShipmentLedger() {
  const { user } = useAuth();
  const token = localStorage.getItem("sendtoo_token");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  const role = user?.role || "customer";
  const isAdmin = role === "admin";

  // Filters
  const [search, setSearch] = useState("");
  const [network, setNetwork] = useState("All");
  const [recon, setRecon] = useState("All");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Data
  const [summary, setSummary] = useState({
    total_shipments: 0, total_revenue: 0, total_vendor_cost: 0,
    total_margin: 0, total_gst: 0, avg_margin_percent: 0,
  });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modal
  const [viewLedger, setViewLedger] = useState(null);

  // Midmile update form
  const [midmileOpen, setMidmileOpen] = useState(false);
  const [mmAmount, setMmAmount] = useState("");
  const [mmUrl, setMmUrl] = useState("");
  const [mmExtras, setMmExtras] = useState("15");
  const [mmSubmitting, setMmSubmitting] = useState(false);

  const buildQuery = useCallback(() => {
    const p = new URLSearchParams();
    if (network !== "All") p.set("network", network);
    if (recon !== "All") p.set("reconciliation_status", recon);
    if (search) p.set("search", search);
    if (dateFrom) p.set("date_from", dateFrom);
    if (dateTo) p.set("date_to", dateTo);
    return p.toString();
  }, [network, recon, search, dateFrom, dateTo]);

  const fetchSummary = useCallback(async () => {
    try {
      const p = new URLSearchParams();
      if (dateFrom) p.set("date_from", dateFrom);
      if (dateTo) p.set("date_to", dateTo);
      const r = await fetch(`${BASE}/admin/summary?${p.toString()}`, { headers });
      if (r.ok) setSummary(await r.json());
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BASE}/admin/list?${buildQuery()}`, { headers });
      if (r.ok) {
        const data = await r.json();
        setItems(data.items || []);
      }
    } catch { /* ignore */ }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildQuery]);

  useEffect(() => { if (isAdmin) fetchSummary(); }, [fetchSummary, isAdmin]);
  useEffect(() => { if (isAdmin) fetchList(); }, [fetchList, isAdmin]);

  const onSearch = () => { fetchList(); fetchSummary(); };

  const openMidmileForm = () => {
    setMmAmount(String(viewLedger?.midmile_charge ?? ""));
    setMmUrl(viewLedger?.midmile_lr_url ?? "");
    setMmExtras(String(viewLedger?.midmile_extras ?? "15"));
    setMidmileOpen(true);
  };

  const submitMidmile = async () => {
    if (!viewLedger) return;
    const amt = parseFloat(mmAmount);
    if (!(amt >= 0)) return toast.error("Enter a valid midmile amount");
    setMmSubmitting(true);
    try {
      const r = await fetch(
        `${BASE}/${viewLedger.shipment_id}/update-midmile`,
        {
          method: "POST", headers,
          body: JSON.stringify({
            midmile_actual_amount: amt,
            midmile_lr_url: mmUrl || null,
            midmile_extras: parseFloat(mmExtras) || 0,
          }),
        }
      );
      if (r.ok) {
        const d = await r.json();
        toast.success(`Midmile updated, charges recalculated (Δ ${fmt(d.difference)})`);
        setMidmileOpen(false);
        // refresh ledger + list
        const g = await fetch(`${BASE}/${viewLedger.shipment_id}`, { headers });
        if (g.ok) setViewLedger(await g.json());
        fetchList();
        fetchSummary();
      } else {
        const err = await r.json().catch(() => ({ detail: "Update failed" }));
        toast.error(err.detail || "Update failed");
      }
    } catch (e) { toast.error(e.message); }
    setMmSubmitting(false);
  };

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="shipment-ledger">
        {/* HEADER */}
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Shipment Ledger</h1>
          <p className="text-sm text-slate-500">Full cost breakdown per shipment</p>
        </div>

        {/* SUMMARY CARDS */}
        {isAdmin && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="summary-cards">
            <StatCard label="Total Shipments" value={summary.total_shipments} icon={FileText} color="text-blue-600" />
            <StatCard label="Total Revenue" value={fmt(summary.total_revenue)} icon={IndianRupee} color="text-emerald-600" />
            <StatCard label="Total Vendor Cost" value={fmt(summary.total_vendor_cost)} icon={TrendingUp} color="text-orange-600" />
            <StatCard label="Total Margin" value={fmt(summary.total_margin)} icon={TrendingUp} color="text-emerald-600" />
            <StatCard label="Avg Margin %" value={`${summary.avg_margin_percent || 0}%`} icon={Percent} color="text-indigo-600" />
          </div>
        )}

        {/* FILTERS */}
        <div className="bg-white border rounded-lg p-3 grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
          <div className="col-span-2">
            <label className="text-[10px] uppercase text-slate-500 font-semibold">Search</label>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)}
                     className="pl-7 h-9 text-sm" placeholder="Shipment ID or AWB..."
                     data-testid="filter-search" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase text-slate-500 font-semibold">Network</label>
            <select value={network} onChange={(e) => setNetwork(e.target.value)}
                    className="w-full border border-slate-300 rounded h-9 px-2 text-sm"
                    data-testid="filter-network">
              {NETWORKS.map((n) => <option key={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase text-slate-500 font-semibold">Recon Status</label>
            <select value={recon} onChange={(e) => setRecon(e.target.value)}
                    className="w-full border border-slate-300 rounded h-9 px-2 text-sm"
                    data-testid="filter-recon">
              {RECON_STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase text-slate-500 font-semibold">From</label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                   className="h-9 text-sm" data-testid="filter-from" />
          </div>
          <div>
            <label className="text-[10px] uppercase text-slate-500 font-semibold">To</label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                   className="h-9 text-sm" data-testid="filter-to" />
          </div>
          <div className="col-span-2 md:col-span-6 flex justify-end">
            <Button onClick={onSearch} className="h-9" data-testid="search-btn">
              <Search className="w-4 h-4 mr-1.5" /> Search
            </Button>
          </div>
        </div>

        {/* TABLE */}
        <div className="bg-white border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-2 py-2 font-semibold">Shipment</th>
                  <th className="text-left px-2 py-2 font-semibold">AWB</th>
                  <th className="text-left px-2 py-2 font-semibold">Network</th>
                  <th className="text-left px-2 py-2 font-semibold">Vendor</th>
                  <th className="text-right px-2 py-2 font-semibold">Actual kg</th>
                  <th className="text-right px-2 py-2 font-semibold">Chargeable kg</th>
                  {isAdmin && <th className="text-right px-2 py-2 font-semibold">Vendor Cost</th>}
                  {isAdmin && <th className="text-right px-2 py-2 font-semibold">Margin %</th>}
                  {isAdmin && <th className="text-right px-2 py-2 font-semibold">Margin ₹</th>}
                  <th className="text-right px-2 py-2 font-semibold">Customer ₹</th>
                  <th className="text-right px-2 py-2 font-semibold">GST ₹</th>
                  <th className="text-right px-2 py-2 font-semibold">Final ₹</th>
                  <th className="text-left px-2 py-2 font-semibold">Midmile</th>
                  <th className="text-left px-2 py-2 font-semibold">Recon</th>
                  <th className="text-right px-2 py-2 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={15} className="py-10 text-center">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" />
                  </td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={15} className="py-10 text-center text-slate-400">
                    No ledger entries found
                  </td></tr>
                ) : (
                  items.map((it) => (
                    <tr key={it.shipment_id} className="border-b border-slate-100 hover:bg-slate-50/50"
                        data-testid={`row-${it.shipment_id}`}>
                      <td className="px-2 py-2 font-mono text-[11px]">{it.shipment_id}</td>
                      <td className="px-2 py-2 font-mono text-[11px]">{it.awb || "—"}</td>
                      <td className="px-2 py-2"><Badge className="text-[10px] bg-slate-100 text-slate-700">{it.network}</Badge></td>
                      <td className="px-2 py-2 text-[11px]">{it.vendor_code}</td>
                      <td className="px-2 py-2 text-right">{it.actual_weight_kg}</td>
                      <td className="px-2 py-2 text-right font-medium">{it.chargeable_weight_kg}</td>
                      {isAdmin && <td className="px-2 py-2 text-right text-orange-700">{fmt(it.vendor_cost)}</td>}
                      {isAdmin && <td className="px-2 py-2 text-right">{it.margin_percent}%</td>}
                      {isAdmin && <td className="px-2 py-2 text-right text-emerald-700">{fmt(it.margin_amount)}</td>}
                      <td className="px-2 py-2 text-right">{fmt(it.customer_price)}</td>
                      <td className="px-2 py-2 text-right text-slate-500">{fmt(it.gst_amount)}</td>
                      <td className="px-2 py-2 text-right font-bold text-blue-700">{fmt(it.final_amount)}</td>
                      <td className="px-2 py-2">
                        <Badge className={`text-[10px] ${MID_BADGE[it.midmile_status] || "bg-slate-100 text-slate-600"}`}>
                          {it.midmile_status || "—"}
                        </Badge>
                      </td>
                      <td className="px-2 py-2">
                        <Badge className={`text-[10px] ${RECON_BADGE[it.reconciliation_status] || "bg-slate-100 text-slate-600"}`}>
                          {it.reconciliation_status || "—"}
                        </Badge>
                      </td>
                      <td className="px-2 py-2 text-right">
                        <Button variant="outline" size="sm" onClick={() => setViewLedger(it)}
                                data-testid={`view-${it.shipment_id}`}>
                          <Eye className="w-3.5 h-3.5 mr-1" /> View
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* COST BREAKDOWN MODAL */}
        <Dialog open={!!viewLedger} onOpenChange={() => setViewLedger(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Cost Breakdown — {viewLedger?.shipment_id}</DialogTitle>
            </DialogHeader>
            {viewLedger && (
              <div className="space-y-4" data-testid="cost-breakdown-modal">
                {(viewLedger.midmile_status === "PENDING" || viewLedger.midmile_status === "SUBMITTED") && (
                  <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-800 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Midmile charges not yet approved. Final amount may change.
                  </div>
                )}

                <div className="grid grid-cols-2 gap-6">
                  {/* LEFT — Weight */}
                  <div className="space-y-2">
                    <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Weight Details</h3>
                    <Row label="Actual Weight" value={`${viewLedger.actual_weight_kg} kg`} />
                    <Row label="Volumetric Weight" value={`${viewLedger.volumetric_weight_kg} kg (divisor: ${viewLedger.volumetric_divisor || 4000})`} />
                    <Row label="Chargeable Weight" value={`${viewLedger.chargeable_weight_kg} kg`} bold />
                    <Row label="FM Distance" value={`${viewLedger.fm_distance_km || 0} km`} />
                    <Row label="LM Distance" value={`${viewLedger.lm_distance_km || 0} km`} />
                  </div>

                  {/* RIGHT — Charges */}
                  <div className="space-y-2">
                    <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Charge Details</h3>
                    <Row label="Base Charge" value={fmt(viewLedger.base_charge)} />
                    <Row label="LR Charge" value={fmt(viewLedger.lr_charge)} />
                    <Row label="First Mile" value={fmt(viewLedger.fm_charge)} />
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-slate-500">Midmile</span>
                      <span className="flex items-center gap-2">
                        <span>{fmt(viewLedger.midmile_charge)}</span>
                        <Badge className={`text-[10px] ${MID_BADGE[viewLedger.midmile_status] || ""}`}>
                          {viewLedger.midmile_status || "—"}
                        </Badge>
                      </span>
                    </div>
                    <Row label="Last Mile" value={fmt(viewLedger.lm_charge)} />
                    <Row label="Fuel Surcharge (20%)" value={fmt(viewLedger.fuel_surcharge)} />
                    <Row label="Insurance" value={fmt(viewLedger.insurance)} />
                    <div className="border-t my-1" />
                    {isAdmin && <Row label="Vendor Cost" value={fmt(viewLedger.vendor_cost)} bold color="text-orange-600" />}
                    {isAdmin && <Row label={`Margin (${viewLedger.margin_percent || 0}%)`} value={fmt(viewLedger.margin_amount)} bold color="text-emerald-600" />}
                    <Row label="Customer Price" value={fmt(viewLedger.customer_price)} />
                    <Row label="GST (18%)" value={fmt(viewLedger.gst_amount)} />
                    <div className="border-t my-1" />
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-slate-700">FINAL AMOUNT</span>
                      <span className="font-bold text-xl text-blue-700">{fmt(viewLedger.final_amount)}</span>
                    </div>
                  </div>
                </div>

                {isAdmin && viewLedger.midmile_status !== "APPROVED" && (
                  <div className="flex justify-end pt-2 border-t">
                    <Button onClick={openMidmileForm}
                            className="bg-emerald-600 hover:bg-emerald-700"
                            data-testid="open-midmile-form">
                      <Upload className="w-4 h-4 mr-1.5" /> Update Midmile
                    </Button>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* MIDMILE UPDATE FORM */}
        <Dialog open={midmileOpen} onOpenChange={setMidmileOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Update Midmile</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Midmile Actual Amount (₹)</label>
                <Input type="number" value={mmAmount} onChange={(e) => setMmAmount(e.target.value)}
                       data-testid="mm-amount" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Midmile LR Photo URL</label>
                <Input value={mmUrl} onChange={(e) => setMmUrl(e.target.value)}
                       placeholder="https://..." data-testid="mm-url" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Extras (₹)</label>
                <Input type="number" value={mmExtras} onChange={(e) => setMmExtras(e.target.value)}
                       data-testid="mm-extras" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMidmileOpen(false)}>Cancel</Button>
              <Button onClick={submitMidmile} disabled={mmSubmitting} data-testid="submit-midmile">
                {mmSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Upload className="w-4 h-4 mr-1.5" />}
                Submit
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-white border rounded-lg p-4">
      <div className="text-xs text-slate-500 flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className={`text-xl font-bold mt-1 ${color}`}>{value}</div>
    </div>
  );
}

function Row({ label, value, bold, color }) {
  return (
    <div className="flex justify-between items-center text-xs">
      <span className={`text-slate-500 ${bold ? "font-semibold" : ""}`}>{label}</span>
      <span className={`${bold ? "font-bold" : "font-medium"} ${color || "text-slate-800"}`}>{value}</span>
    </div>
  );
}
